import { useState, useEffect, useCallback, useRef } from "react";
import { clientPollingManager } from "../services/clientPolling.service";

/**
 * Hook React para gerenciar Client Polling
 * Substitui o useSSE, mantendo interface similar para facilitar migração
 */
export default function useClientPolling() {
  const [isConnected, setIsConnected] = useState(false);
  const [progresso, setProgresso] = useState(null);
  const [erro, setErro] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const versaoIdRef = useRef(null);

  /**
   * Limpa todos os estados
   */
  const limparEstado = useCallback(() => {
    setProgresso(null);
    setErro(null);
    setResultado(null);
    setLoading(false);
    setIsConnected(false);
  }, []);

  /**
   * Cria versão e inicia execução do motor
   */
  const criarVersaoComMotor = useCallback(async (payload) => {
    try {
      limparEstado();
      setLoading(true);
      setIsConnected(true);

      const response = await clientPollingManager.criarVersaoComMotor(payload);
      
      // Extrair versaoId da resposta (pode vir em diferentes formatos)
      const versaoId = response.versaoId || response.versao?.id || response.versao?.versaoId || response.id || response.dados?.versaoId;
      versaoIdRef.current = versaoId;

      // Inicia polling automático
      clientPollingManager.iniciarPolling(
        versaoId,
        // onProgress
        (status) => {
          setProgresso(status);
        },
        // onComplete
        (status) => {
          setResultado(status);
          setLoading(false);
          setIsConnected(false);
        },
        // onError
        (error) => {
          setErro(error);
          setLoading(false);
          setIsConnected(false);
        }
      );

      return response;
    } catch (error) {
      setErro(error);
      setLoading(false);
      setIsConnected(false);
      throw error;
    }
  }, [limparEstado]);

  /**
   * Executa motor para uma versão existente
   */
  const executarMotor = useCallback(async (versaoId) => {
    try {
      limparEstado();
      setLoading(true);
      setIsConnected(true);
      versaoIdRef.current = versaoId;

      const response = await clientPollingManager.executarMotor(versaoId);

      // Inicia polling automático
      clientPollingManager.iniciarPolling(
        versaoId,
        // onProgress
        (status) => {
          setProgresso(status);
        },
        // onComplete
        (status) => {
          setResultado(status);
          setLoading(false);
          setIsConnected(false);
        },
        // onError
        (error) => {
          setErro(error);
          setLoading(false);
          setIsConnected(false);
        }
      );

      return response;
    } catch (error) {
      setErro(error);
      setLoading(false);
      setIsConnected(false);
      throw error;
    }
  }, [limparEstado]);

  /**
   * Inicia acompanhamento de progresso para uma versão existente
   */
  const acompanharProgresso = useCallback(async (versaoId) => {
    try {
      limparEstado();
      setLoading(true);
      setIsConnected(true);
      versaoIdRef.current = versaoId;

      // Inicia polling automático
      clientPollingManager.iniciarPolling(
        versaoId,
        // onProgress
        (status) => {
          setProgresso(status);
          setLoading(false); // Remove loading após primeira resposta
        },
        // onComplete
        (status) => {
          setResultado(status);
          setLoading(false);
          setIsConnected(false);
        },
        // onError
        (error) => {
          setErro(error);
          setLoading(false);
          setIsConnected(false);
        }
      );
    } catch (error) {
      setErro(error);
      setLoading(false);
      setIsConnected(false);
      throw error;
    }
  }, [limparEstado]);

  /**
   * Desconecta e para todos os pollings
   */
  const desconectar = useCallback(() => {
    if (versaoIdRef.current) {
      clientPollingManager.pararPolling(versaoIdRef.current);
    }
    setIsConnected(false);
    setLoading(false);
  }, []);

  /**
   * Cleanup ao desmontar componente
   */
  useEffect(() => {
    return () => {
      if (versaoIdRef.current) {
        clientPollingManager.pararPolling(versaoIdRef.current);
      }
    };
  }, []);

  return {
    isConnected,
    progresso,
    erro,
    resultado,
    loading,
    criarVersaoComMotor,
    executarMotor,
    acompanharProgresso,
    desconectar,
    limparEstado,
  };
}
